import React, { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Spinner } from "react-bootstrap"
import PortAlertService, {
  BerthData,
  LocationSelection,
  PortAlert,
} from "../hooks/portAlertsService"
import { CountryUNLocodeList } from "../helpers/countryUnLocode"
import { isBerthExcluded, formatBerthId } from "../helpers/portAlertHelpers"

interface LocationPickerProps {
  value: LocationSelection | null
  onChange: (selection: LocationSelection | null) => void
  existingAlerts?: PortAlert[]
}

type BrowseLevel = "countries" | "ports" | "terminals" | "berths"

interface BrowseState {
  level: BrowseLevel
  country?: { code: string; name: string }
  port?: { unloc: string; name: string }
  terminal?: { id: string; name: string }
}

interface DerivedPort {
  portUnloc: string
  portName: string
}

interface DerivedTerminal {
  terminalId: string
  terminalName: string
  portUnloc: string
}

const LocationPicker: React.FC<LocationPickerProps> = ({ value, onChange, existingAlerts = [] }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [browseState, setBrowseState] = useState<BrowseState>({ level: "countries" })

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const portAlertService = PortAlertService()

  const { data: rawBerthsData, isLoading: isBerthsLoading } = useQuery(
    ["portalerts-berths"],
    () => portAlertService.getBerths(),
    { staleTime: 5 * 60 * 1000 }
  )

  const berthsData = useMemo(() => {
    if (!rawBerthsData) return []
    return rawBerthsData.filter((b) => !isBerthExcluded(b.berth_id))
  }, [rawBerthsData])

  const derivedPorts = useMemo(() => {
    const map = new Map<string, DerivedPort>()
    for (const b of berthsData) {
      if (!b.port_unloc) continue
      if (map.has(b.port_unloc)) continue
      map.set(b.port_unloc, {
        portUnloc: b.port_unloc,
        portName: b.port_name || b.port_unloc,
      })
    }
    return Array.from(map.values())
  }, [berthsData])

  const derivedTerminals = useMemo(() => {
    const map = new Map<string, DerivedTerminal>()
    for (const b of berthsData) {
      if (!b.terminal_id || !b.terminal_name) continue
      if (map.has(b.terminal_id)) continue
      map.set(b.terminal_id, {
        terminalId: b.terminal_id,
        terminalName: b.terminal_name,
        portUnloc: b.port_unloc || "",
      })
    }
    return Array.from(map.values())
  }, [berthsData])

  const portsByCountry = useMemo(() => {
    const map = new Map<string, DerivedPort[]>()
    for (const p of derivedPorts) {
      const countryCode = p.portUnloc.substring(0, 2)
      if (!countryCode) continue
      const existing = map.get(countryCode) || []
      existing.push(p)
      map.set(countryCode, existing)
    }
    return map
  }, [derivedPorts])

  const terminalsByPort = useMemo(() => {
    const map = new Map<string, DerivedTerminal[]>()
    for (const t of derivedTerminals) {
      if (!t.portUnloc) continue
      const existing = map.get(t.portUnloc) || []
      existing.push(t)
      map.set(t.portUnloc, existing)
    }
    return map
  }, [derivedTerminals])

  const berthsByTerminal = useMemo(() => {
    const map = new Map<string, BerthData[]>()
    for (const b of berthsData) {
      if (!b.terminal_id) continue
      const existing = map.get(b.terminal_id) || []
      existing.push(b)
      map.set(b.terminal_id, existing)
    }
    return map
  }, [berthsData])

  const isAlertExists = useMemo(() => {
    const set = new Set(
      existingAlerts.map(a =>
        [a.countryUnlocode, a.port || '', a.terminal || '', a.berth || ''].join('|')
      )
    )
    return (countryCode: string, port = '', terminal = '', berth = '') =>
      set.has([countryCode, port, terminal, berth].join('|'))
  }, [existingAlerts])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearchQuery("")
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const scrollDropdownToTop = () => {
    requestAnimationFrame(() => {
      dropdownRef.current?.scrollTo(0, 0)
    })
  }

  const handleSelect = (selection: LocationSelection) => {
    onChange(selection)
    setIsOpen(false)
    setSearchQuery("")
    setBrowseState({ level: "countries" })
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setSearchQuery("")
    setBrowseState({ level: "countries" })
  }

  const handleInputFocus = () => {
    setIsOpen(true)
    scrollDropdownToTop()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    if (!isOpen) setIsOpen(true)
  }

  const drillIntoCountry = (e: React.MouseEvent, code: string, name: string) => {
    e.stopPropagation()
    setBrowseState({ level: "ports", country: { code, name } })
    scrollDropdownToTop()
  }

  const drillIntoPort = (e: React.MouseEvent, unloc: string, name: string, countryCode: string, countryName: string) => {
    e.stopPropagation()
    setBrowseState({
      level: "terminals",
      country: { code: countryCode, name: countryName },
      port: { unloc, name },
    })
    scrollDropdownToTop()
  }

  const drillIntoTerminal = (
    e: React.MouseEvent,
    id: string,
    name: string,
    portUnloc: string,
    portName: string,
    countryCode: string,
    countryName: string
  ) => {
    e.stopPropagation()
    setBrowseState({
      level: "berths",
      country: { code: countryCode, name: countryName },
      port: { unloc: portUnloc, name: portName },
      terminal: { id, name },
    })
    scrollDropdownToTop()
  }

  const countries = CountryUNLocodeList
  const isSearching = searchQuery.trim().length > 0

  const searchResults = useMemo(() => {
    if (!isSearching) return null
    const q = searchQuery.toLowerCase()

    const matchedCountries = countries
      .filter((c) => c.label.toLowerCase().includes(q) || c.value.toLowerCase().includes(q))
      .slice(0, 10)

    const matchedPorts: (DerivedPort & { countryCode: string; countryName: string })[] = []
    for (const p of derivedPorts) {
      if (p.portName.toLowerCase().includes(q) || p.portUnloc.toLowerCase().includes(q)) {
        const countryCode = p.portUnloc.substring(0, 2)
        const country = countries.find((c) => c.value === countryCode)
        matchedPorts.push({ ...p, countryCode, countryName: country?.label || countryCode })
      }
      if (matchedPorts.length >= 10) break
    }

    const matchedTerminals: (DerivedTerminal & { countryCode: string; countryName: string; portName: string })[] = []
    for (const t of derivedTerminals) {
      if (t.terminalName.toLowerCase().includes(q)) {
        const countryCode = t.portUnloc.substring(0, 2)
        const country = countries.find((c) => c.value === countryCode)
        const port = derivedPorts.find((p) => p.portUnloc === t.portUnloc)
        matchedTerminals.push({ ...t, countryCode, countryName: country?.label || countryCode, portName: port?.portName || t.portUnloc })
      }
      if (matchedTerminals.length >= 10) break
    }

    const matchedBerths: (BerthData & { countryCode: string; countryName: string })[] = []
    for (const b of berthsData) {
      if (b.berth_id.toLowerCase().includes(q) || (b.terminal_name || "").toLowerCase().includes(q)) {
        const countryCode = (b.port_unloc || "").substring(0, 2)
        const country = countries.find((c) => c.value === countryCode)
        matchedBerths.push({ ...b, countryCode, countryName: country?.label || countryCode })
      }
      if (matchedBerths.length >= 10) break
    }

    return { matchedCountries, matchedPorts, matchedTerminals, matchedBerths }
  }, [isSearching, searchQuery, countries, derivedPorts, derivedTerminals, berthsData])

  const breadcrumbs = useMemo(() => {
    const items: { label: string; onClick: () => void }[] = [
      { label: "All countries", onClick: () => setBrowseState({ level: "countries" }) },
    ]
    if (browseState.country) {
      items.push({
        label: browseState.country.name,
        onClick: () => setBrowseState({ level: "ports", country: browseState.country }),
      })
    }
    if (browseState.port) {
      items.push({
        label: browseState.port.name,
        onClick: () => setBrowseState({ level: "terminals", country: browseState.country, port: browseState.port }),
      })
    }
    if (browseState.terminal) {
      items.push({
        label: browseState.terminal.name,
        onClick: () => setBrowseState({ ...browseState, level: "berths" }),
      })
    }
    return items
  }, [browseState])

  const renderBrowseContent = () => {
    if (browseState.level === "countries") {
      return (
        <div className="location-picker__list">
          <div className="location-picker__group-header">Countries</div>
          {countries.filter((c) => !isAlertExists(c.value)).map((c) => {
            const hasPorts = portsByCountry.has(c.value)
            return (
              <div key={c.value} className="location-picker__item">
                <span
                  className="location-picker__item-label"
                  onClick={() =>
                    handleSelect({ type: "country", countryUnlocode: c.value, countryName: c.label })
                  }
                >
                  {c.label}
                </span>
                {hasPorts && (
                  <span
                    className="location-picker__drill"
                    onClick={(e) => drillIntoCountry(e, c.value, c.label)}
                  >
                    ›
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )
    }

    if (browseState.level === "ports" && browseState.country) {
      const ports = portsByCountry.get(browseState.country.code) || []

      return (
        <div className="location-picker__list">
          <div className="location-picker__group-header">Ports</div>
          {ports.length === 0 ? (
            <div className="location-picker__empty">No ports found</div>
          ) : (
            ports.filter((p) => !isAlertExists(browseState.country!.code, p.portName)).map((p) => {
              const hasTerminals = terminalsByPort.has(p.portUnloc)
              return (
                <div key={p.portUnloc} className="location-picker__item">
                  <span
                    className="location-picker__item-label"
                    onClick={() =>
                      handleSelect({
                        type: "port",
                        countryUnlocode: browseState.country!.code,
                        countryName: browseState.country!.name,
                        port: p.portName,
                        portUnlocode: p.portUnloc,
                      })
                    }
                  >
                    {p.portName}
                  </span>
                  {hasTerminals && (
                    <span
                      className="location-picker__drill"
                      onClick={(e) =>
                        drillIntoPort(e, p.portUnloc, p.portName, browseState.country!.code, browseState.country!.name)
                      }
                    >
                      ›
                    </span>
                  )}
                </div>
              )
            })
          )}
        </div>
      )
    }

    if (browseState.level === "terminals" && browseState.port && browseState.country) {
      const terminals = terminalsByPort.get(browseState.port.unloc) || []

      return (
        <div className="location-picker__list">
          <div className="location-picker__group-header">Terminals</div>
          {terminals.length === 0 ? (
            <div className="location-picker__empty">No terminals found</div>
          ) : (
            terminals.filter((t) => !isAlertExists(browseState.country!.code, browseState.port!.name, t.terminalName)).map((t) => {
              const hasBerths = berthsByTerminal.has(t.terminalId)
              return (
                <div key={t.terminalId} className="location-picker__item">
                  <div
                    className="location-picker__item-label"
                    onClick={() =>
                      handleSelect({
                        type: "terminal",
                        countryUnlocode: browseState.country!.code,
                        countryName: browseState.country!.name,
                        port: browseState.port!.name,
                        portUnlocode: browseState.port!.unloc,
                        terminal: t.terminalName,
                        terminalId: t.terminalId,
                      })
                    }
                  >
                    <span>{t.terminalName}</span>
                  </div>
                  {hasBerths && (
                    <span
                      className="location-picker__drill"
                      onClick={(e) =>
                        drillIntoTerminal(
                          e,
                          t.terminalId,
                          t.terminalName,
                          browseState.port!.unloc,
                          browseState.port!.name,
                          browseState.country!.code,
                          browseState.country!.name
                        )
                      }
                    >
                      ›
                    </span>
                  )}
                </div>
              )
            })
          )}
        </div>
      )
    }

    if (browseState.level === "berths" && browseState.terminal && browseState.port && browseState.country) {
      const berths = berthsByTerminal.get(browseState.terminal.id) || []

      return (
        <div className="location-picker__list">
          <div className="location-picker__group-header">Berths</div>
          {berths.length === 0 ? (
            <div className="location-picker__empty">No berths found</div>
          ) : (
            berths.filter((b) => !isAlertExists(browseState.country!.code, browseState.port!.name, browseState.terminal!.name, b.berth_id)).map((b) => (
              <div key={b.berth_id} className="location-picker__item">
                <span
                  className="location-picker__item-label"
                  onClick={() =>
                    handleSelect({
                      type: "berth",
                      countryUnlocode: browseState.country!.code,
                      countryName: browseState.country!.name,
                      port: browseState.port!.name,
                      portUnlocode: browseState.port!.unloc,
                      terminal: b.terminal_name || browseState.terminal!.name,
                      terminalId: b.terminal_id || browseState.terminal!.id,
                      berth: b.berth_id,
                    })
                  }
                >
                  {formatBerthId(b.berth_id)}
                </span>
              </div>
            ))
          )}
        </div>
      )
    }

    return null
  }

  const renderSearchContent = () => {
    if (!searchResults) return null
    const matchedCountries = searchResults.matchedCountries.filter((c) => !isAlertExists(c.value))
    const matchedPorts = searchResults.matchedPorts.filter((p) => !isAlertExists(p.countryCode, p.portName))
    const matchedTerminals = searchResults.matchedTerminals.filter((t) => !isAlertExists(t.countryCode, t.portName, t.terminalName))
    const matchedBerths = searchResults.matchedBerths.filter((b) => !isAlertExists(b.countryCode, b.port_name || '', b.terminal_name || '', b.berth_id))
    const hasResults = matchedCountries.length > 0 || matchedPorts.length > 0 || matchedTerminals.length > 0 || matchedBerths.length > 0

    if (!hasResults) return <div className="location-picker__empty">No results found</div>

    return (
      <div className="location-picker__list">
        {matchedCountries.length > 0 && (
          <>
            <div className="location-picker__group-header">Countries</div>
            {matchedCountries.map((c) => (
              <div key={c.value} className="location-picker__item">
                <span
                  className="location-picker__item-label"
                  onClick={() =>
                    handleSelect({ type: "country", countryUnlocode: c.value, countryName: c.label })
                  }
                >
                  {c.label}
                </span>
              </div>
            ))}
          </>
        )}
        {matchedPorts.length > 0 && (
          <>
            <div className="location-picker__group-header">Ports</div>
            {matchedPorts.map((p) => (
              <div key={p.portUnloc} className="location-picker__item">
                <div
                  className="location-picker__item-label"
                  onClick={() =>
                    handleSelect({
                      type: "port",
                      countryUnlocode: p.countryCode,
                      countryName: p.countryName,
                      port: p.portName,
                      portUnlocode: p.portUnloc,
                    })
                  }
                >
                  <span>{p.portName}</span>
                  <span className="location-picker__item-sub">{p.countryName}</span>
                </div>
              </div>
            ))}
          </>
        )}
        {matchedTerminals.length > 0 && (
          <>
            <div className="location-picker__group-header">Terminals</div>
            {matchedTerminals.map((t) => (
              <div key={t.terminalId} className="location-picker__item">
                <div
                  className="location-picker__item-label"
                  onClick={() =>
                    handleSelect({
                      type: "terminal",
                      countryUnlocode: t.countryCode,
                      countryName: t.countryName,
                      port: t.portName,
                      portUnlocode: t.portUnloc,
                      terminal: t.terminalName,
                      terminalId: t.terminalId,
                    })
                  }
                >
                  <span>{t.terminalName}</span>
                  <span className="location-picker__item-sub">{t.countryName} › {t.portName}</span>
                </div>
              </div>
            ))}
          </>
        )}
        {matchedBerths.length > 0 && (
          <>
            <div className="location-picker__group-header">Berths</div>
            {matchedBerths.map((b) => (
              <div key={`${b.berth_id}-${b.terminal_id || ""}`} className="location-picker__item">
                <div
                  className="location-picker__item-label"
                  onClick={() =>
                    handleSelect({
                      type: "berth",
                      countryUnlocode: b.countryCode,
                      countryName: b.countryName,
                      port: b.port_name,
                      portUnlocode: b.port_unloc,
                      terminal: b.terminal_name,
                      terminalId: b.terminal_id,
                      berth: b.berth_id,
                    })
                  }
                >
                  <span>{formatBerthId(b.berth_id)}</span>
                  <span className="location-picker__item-sub">
                    {b.countryName}{b.port_name ? ` › ${b.port_name}` : ""}{b.terminal_name ? ` › ${b.terminal_name}` : ""}
                  </span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    )
  }

  const displayValue = value
    ? value.type === "berth"
      ? formatBerthId(value.berth || "")
      : value.type === "terminal"
      ? value.terminal
      : value.type === "port"
      ? value.port
      : value.countryName
    : ""

  const selectionTag = value
    ? value.type === "berth"
      ? `Berth: ${formatBerthId(value.berth || "")}`
      : value.type === "terminal"
      ? `Terminal: ${value.terminal}`
      : value.type === "port"
      ? `Port: ${value.port}`
      : `Country: ${value.countryName}`
    : null

  return (
    <div className="location-picker" ref={containerRef}>
      <div className="location-picker__control" onClick={() => inputRef.current?.focus()}>
        <input
          ref={inputRef}
          className="location-picker__input"
          placeholder={value ? displayValue : "Select a location"}
          value={isOpen ? searchQuery : value ? displayValue : ""}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          readOnly={!isOpen}
        />
        {value && (
          <span className="location-picker__clear" onClick={handleClear}>
            ×
          </span>
        )}
        {isBerthsLoading ? (
          <Spinner animation="border" size="sm" style={{ color: "#9ca3af", marginRight: 2 }} />
        ) : (
          <span className="location-picker__indicator">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M4.516 7.548c.436-.446 1.043-.481 1.576 0L10 11.295l3.908-3.747c.533-.481 1.141-.446 1.574 0 .436.445.408 1.197 0 1.615-.406.418-4.695 4.502-4.695 4.502a1.095 1.095 0 0 1-1.576 0S4.924 9.581 4.516 9.163c-.409-.418-.436-1.17 0-1.615z" />
            </svg>
          </span>
        )}
      </div>

      {value && selectionTag && (
        <div className="location-picker__tag">
          <span>{selectionTag}</span>
          <span className="location-picker__tag-clear" onClick={handleClear}>
            ✕
          </span>
        </div>
      )}

      {isOpen && (
        <div className="location-picker__dropdown" ref={dropdownRef}>
          {!isSearching && browseState.level !== "countries" && (
            <div className="location-picker__breadcrumbs">
              {breadcrumbs.map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="location-picker__breadcrumb-sep">›</span>}
                  <span
                    className={`location-picker__breadcrumb ${
                      i === breadcrumbs.length - 1 ? "location-picker__breadcrumb--active" : ""
                    }`}
                    onClick={b.onClick}
                  >
                    {b.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          {isBerthsLoading && (
            <div className="location-picker__empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading locations...
            </div>
          )}
          {!isBerthsLoading && (isSearching ? renderSearchContent() : renderBrowseContent())}
        </div>
      )}
    </div>
  )
}

export default LocationPicker
